import { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getTreatment } from "@/data/treatments";
import SEO from "@/components/SEO";

const ImageCarousel = ({ images, name, fit = "contain", aspect = "4/3" }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = images.length;
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    if (paused || total <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % total), 3000);
    return () => clearInterval(id);
  }, [paused, total]);

  const go = (dir) => setIdx((i) => (i + dir + total) % total);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; touchDeltaX.current = 0; };
  const onTouchMove = (e) => { if (touchStartX.current != null) touchDeltaX.current = e.touches[0].clientX - touchStartX.current; };
  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null; touchDeltaX.current = 0;
  };

  const isCover = fit === "cover";
  const maxWidth = aspect === "1/1" ? "max-w-[560px]" : "max-w-[720px]";
  const aspectClass = aspect === "1/1" ? "aspect-square" : "aspect-[4/3]";
  const containerBg = isCover ? "bg-[#F3EFEA]" : "bg-[#F3EFEA]";
  const imgPad = isCover ? "" : "p-4 md:p-6";
  const imgFit = isCover ? "object-cover" : "object-contain";

  return (
    <div
      data-testid="treatment-carousel"
      className={`relative mx-auto w-full ${maxWidth}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className={`relative rounded-[28px] overflow-hidden ${containerBg} border border-[#E5E0DA] shadow-[0_10px_40px_rgba(0,0,0,0.05)] ${aspectClass}`}>
        {images.map((src, i) => {
          let translate = "translate-x-full";
          if (i === idx) translate = "translate-x-0";
          else if (i === (idx - 1 + total) % total) translate = "-translate-x-full";
          return (
            <img
              key={src}
              src={src}
              alt={`${name} ${i + 1}`}
              className={`absolute inset-0 w-full h-full ${imgFit} ${imgPad} transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] ${translate}`}
              draggable={false}
            />
          );
        })}
        {total > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous" className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur border border-[#E5E0DA] flex items-center justify-center hover:bg-white transition-colors z-10">
              <ChevronLeft strokeWidth={1.5} className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next" className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur border border-[#E5E0DA] flex items-center justify-center hover:bg-white transition-colors z-10">
              <ChevronRight strokeWidth={1.5} className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          {images.map((_, i) => (
            <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#1A1A1A]" : "w-1.5 bg-[#D8D2CB] hover:bg-[#8A847C]"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#E5E0DA] rounded-[24px] p-8 md:p-10 flex flex-col justify-between min-h-[170px]">
    <div className="text-[0.78rem] text-[#8A847C] font-medium tracking-normal">{label}</div>
    <div className="text-2xl md:text-[1.9rem] tracking-[-0.02em] text-[#1A1A1A] font-medium leading-tight mt-6">{value}</div>
  </div>
);

const SectionBlock = ({ label, title, children, testid }) => (
  <div data-testid={testid} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 py-16 md:py-20">
    <div className="lg:col-span-4">
      <div className="overline mb-4">{label}</div>
      <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">{title}</h3>
    </div>
    <div className="lg:col-span-8">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-4">
    {items.map((it) => (
      <li key={it} className="flex gap-4 text-[#1A1A1A]">
        <span className="mt-2 w-1 h-1 rounded-full bg-[#8A847C] shrink-0" />
        <span className="leading-relaxed">{it}</span>
      </li>
    ))}
  </ul>
);

export default function TreatmentDetail() {
  const { slug } = useParams();
  const t = getTreatment(slug);
  if (!t) return <Navigate to="/treatments" replace />;

  const priceDisplay = t.priceNote ? `${t.price} · ${t.priceNote}` : t.price;

  return (
    <div data-testid="treatment-detail-page" className="pt-32 md:pt-40 bg-[#FAF8F5]">
      <SEO
        title={`${t.name} | Natalia Medical Aesthetics, Kirkland WA`}
        description={`${t.tagline} ${t.price} at Natalia Medical Aesthetics in Kirkland, WA. Book your personalized consultation.`}
        path={`/treatments/${t.slug}`}
      />
      {/* Header */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <Link to="/treatments" className="text-[0.85rem] font-medium text-[#8A847C] hover:text-[#1A1A1A] flex items-center gap-2 mb-10">
          <ChevronLeft strokeWidth={1.25} className="w-4 h-4" /> All Treatments
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-6">Treatment</div>
            <h1 data-testid="treatment-title" className="font-serif text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight">{t.name}</h1>
            <p className="mt-8 text-[1.05rem] text-[#5C5C5C] leading-relaxed max-w-2xl">{t.tagline}</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/book" data-testid="treatment-book-cta" className="btn-primary">Book Consultation</Link>
          </div>
        </div>
      </section>

      {/* Images — only user-uploaded */}
      {t.gallery && t.gallery.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 mt-14 md:mt-20">
          <ImageCarousel images={t.gallery} name={t.name} fit={t.imageFit || "contain"} aspect={t.imageAspect || "4/3"} />
        </section>
      )}

      {/* Overview info cards */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Overview</div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">Treatment overview</h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[#1A1A1A] text-lg leading-relaxed">{t.overview}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-16">
          {t.time && <InfoCard label="Treatment Time" value={t.time} testid="info-time" />}
          <InfoCard label="Price" value={priceDisplay} testid="info-price" />
          <InfoCard label="Downtime" value={t.downtime} testid="info-downtime" />
          <InfoCard label="Sessions" value={t.sessions} testid="info-sessions" />
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        <SectionBlock testid="section-how" label="Method" title="How it works">
          <p className="text-[#1A1A1A] text-lg leading-relaxed max-w-2xl">{t.howItWorks}</p>
        </SectionBlock>

        <SectionBlock testid="section-best-for" label="Indications" title="Best for">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            {t.bestFor.map((b) => (
              <div key={b} className="bg-white border border-[#E5E0DA] rounded-[20px] px-6 py-4 text-[#1A1A1A]">{b}</div>
            ))}
          </div>
        </SectionBlock>

        <SectionBlock testid="section-benefits" label="Outcome" title="Benefits">
          <List items={t.benefits} />
        </SectionBlock>

        <SectionBlock testid="section-results" label="Timeline" title="Results timeline">
          <p className="text-[#1A1A1A] text-lg leading-relaxed max-w-2xl">{t.resultsTimeline}</p>
        </SectionBlock>

        <SectionBlock testid="section-prep" label="Before" title="Preparation">
          <List items={t.preparation} />
        </SectionBlock>

        <SectionBlock testid="section-aftercare" label="After" title="Aftercare">
          <List items={t.aftercare} />
        </SectionBlock>

        <SectionBlock testid="section-not-candidate" label="Please note" title="Who is not a candidate">
          <List items={t.notCandidate} />
        </SectionBlock>
      </section>

      {/* FAQ */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 border-t border-[#E5E0DA]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-4">
            <div className="overline mb-4">Questions</div>
            <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">Frequently asked</h3>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible data-testid="treatment-faq">
              <AccordionItem value="q1" className="border-b border-[#E5E0DA]">
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl tracking-tight hover:no-underline py-6">
                  Is {t.name} painful?
                </AccordionTrigger>
                <AccordionContent className="text-[#5C5C5C] leading-relaxed pb-6">
                  Most clients describe the sensation as very tolerable. Topical numbing is applied where appropriate to ensure your comfort throughout the treatment.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="q2" className="border-b border-[#E5E0DA]">
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl tracking-tight hover:no-underline py-6">
                  How soon can I return to normal activities?
                </AccordionTrigger>
                <AccordionContent className="text-[#5C5C5C] leading-relaxed pb-6">
                  Downtime is typically {t.downtime.toLowerCase()}. You will receive detailed guidance so you know exactly what to expect on the day of treatment and after.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="q3" className="border-b border-[#E5E0DA]">
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl tracking-tight hover:no-underline py-6">
                  How many sessions will I need?
                </AccordionTrigger>
                <AccordionContent className="text-[#5C5C5C] leading-relaxed pb-6">
                  A typical course is {t.sessions.toLowerCase()}. Your bespoke plan is confirmed during consultation based on your skin and goals.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="bg-[#1A1A1A] text-white rounded-[28px] p-12 md:p-24 text-center">
          <div className="overline !text-[#8A847C] mb-8">Begin</div>
          <h3 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Book a private consultation <em className="italic">for {t.name}.</em>
          </h3>
          <div className="mt-12">
            <Link to="/book" data-testid="treatment-detail-cta" className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] rounded-full px-10 py-5 text-[0.85rem] font-medium font-medium hover:bg-[#F3EFEA] transition-colors">
              Book Consultation <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
